import React, { useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import { ProfitAdjustmentDto } from "../../../models/profitAdjustments";
import { ProfitAdjustmentsApi } from "./profitAdjustmentsApi";
import { NotificationService } from "../../../common/notification";
import { kopecksToRublesNumber, rublesToKopecksNumber } from "../../../common/money";

interface ProfitAdjustmentEditorProps {
    open: boolean;
    mode: "create" | "edit";
    adjustment: ProfitAdjustmentDto | null;
    onClose: () => void;
    onSaved: () => Promise<void> | void;
}

const toInputDateTime = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    const local = new Date(d.getTime() - tzOffsetMs);
    return local.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
};

export const ProfitAdjustmentEditor: React.FC<ProfitAdjustmentEditorProps> = ({
    open,
    mode,
    adjustment,
    onClose,
    onSaved,
}) => {
    const [amountRub, setAmountRub] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [dateInput, setDateInput] = useState<string>("");
    const [userIdInput, setUserIdInput] = useState<string>("");
    const [saving, setSaving] = useState(false);

    React.useEffect(() => {
        if (!open) return;
        if (mode === "edit" && adjustment) {
            setAmountRub(kopecksToRublesNumber(adjustment.amount).toString());
            setComment(adjustment.comment ?? "");
            setDateInput(toInputDateTime(adjustment.date));
            setUserIdInput(adjustment.userId != null ? String(adjustment.userId) : "");
        } else if (mode === "create") {
            setAmountRub("");
            setComment("");
            setDateInput("");
            setUserIdInput("");
        }
    }, [open, mode, adjustment]);

    const handleSave = async () => {
        if (mode === "edit" && !adjustment) return;

        // Валидация userId
        let userIdForRequest: string | null | undefined = undefined;
        const trimmedUserId = (userIdInput ?? "").toString().trim();
        if (trimmedUserId.length > 0) {
            try {
                // Проверим корректность формата
                // eslint-disable-next-line no-unused-expressions
                BigInt(trimmedUserId);
                userIdForRequest = trimmedUserId;
            } catch {
                NotificationService.alertError("Некорректный формат UserId");
                return;
            }
        } else {
            userIdForRequest = null;
        }

        // Парсим сумму
        let amountKopecks: number | null = null;
        const trimmedAmount = amountRub.trim();
        if (trimmedAmount.length > 0) {
            const parsed = rublesToKopecksNumber(trimmedAmount);
            if (Number.isNaN(parsed)) {
                NotificationService.alertError("Некорректный формат суммы");
                return;
            }
            amountKopecks = parsed;
        } else {
            amountKopecks = null;
        }

        // Дата
        let dateIso: string | null = null;
        if (dateInput) {
            const d = new Date(dateInput);
            if (Number.isNaN(d.getTime())) {
                NotificationService.alertError("Некорректная дата");
                return;
            }
            dateIso = d.toISOString();
        } else {
            dateIso = null;
        }

        setSaving(true);
        try {
            let success = false;

            if (mode === "edit" && adjustment) {
                const result = await ProfitAdjustmentsApi.update({
                    id: adjustment.id,
                    comment: comment,
                    amount: amountKopecks,
                    date: dateIso,
                    userId: userIdForRequest,
                });
                success = result.result;
                if (!success) {
                    NotificationService.alertError("Не удалось сохранить корректировку. Пожалуйста попробуйте позже.");
                    return;
                }
                NotificationService.alertSuccess("Корректировка успешно обновлена");
            } else if (mode === "create") {
                if (amountKopecks === null) {
                    NotificationService.alertError("Укажите сумму корректировки");
                    return;
                }
                const createResult = await ProfitAdjustmentsApi.create({
                    amount: amountKopecks,
                    comment: comment || undefined,
                    date: dateIso,
                    userId: userIdForRequest,
                });
                success = createResult.result;
                if (!success) {
                    NotificationService.alertError("Не удалось создать корректировку. Пожалуйста попробуйте позже.");
                    return;
                }
                NotificationService.alertSuccess("Корректировка успешно создана");
            }

            await onSaved();
            onClose();
        } catch (e) {
            console.error(e);
            NotificationService.alertError("Не удалось сохранить корректировку. Пожалуйста попробуйте позже.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog>
                <ModalClose />
                <Typography level="h4" sx={{ mb: 1 }}>
                    {mode === "edit" && adjustment
                        ? `Редактировать корректировку #${adjustment.id}`
                        : "Новая корректировка"}
                </Typography>
                <Sheet
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        maxHeight: "70vh",
                        overflowY: "auto",
                        mt: 1,
                    }}
                >
                    <Stack spacing={1.5}>
                        <FormControl>
                            <FormLabel>Сумма (₽)</FormLabel>
                            <Input
                                value={amountRub}
                                onChange={(e) => setAmountRub(e.target.value)}
                                placeholder="Например, 1234.56"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Комментарий</FormLabel>
                            <Input
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Комментарий"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Дата корректировки</FormLabel>
                            <Input
                                type="datetime-local"
                                value={dateInput}
                                onChange={(e) => setDateInput(e.target.value)}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Юзер ID</FormLabel>
                            <Input
                                value={userIdInput}
                                onChange={(e) => setUserIdInput(e.target.value)}
                                placeholder={
                                    mode === "edit"
                                        ? "Оставьте пустым, чтобы не изменять"
                                        : "Оставьте пустым, если без пользователя"
                                }
                            />
                        </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
                        <Button variant="outlined" color="neutral" onClick={onClose} disabled={saving}>
                            Отмена
                        </Button>
                        <Button color="primary" onClick={handleSave} loading={saving}>
                            Сохранить
                        </Button>
                    </Stack>
                </Sheet>
            </ModalDialog>
        </Modal>
    );
};

